/**
 * INLINE_SELECT_MENU_PROPS — Enterprise Design System
 *
 * Shared MUI `MenuProps` for inline table/panel `<Select>` and
 * `<TextField select>` dropdowns.
 *
 * Why this exists:
 *   Inline dropdowns rendered inside dense tables (e.g. a status editor in a
 *   grid cell) default to MUI positioning with no elevation, which lets the
 *   popup overflow the row and overlap neighbouring rows / pagination. This
 *   config anchors the menu directly beneath the trigger and gives it a
 *   contained, elevated, scrollable surface.
 *
 * Usage:
 *   // On a MUI <Select>
 *   <Select MenuProps={INLINE_SELECT_MENU_PROPS} ... />
 *
 *   // On a MUI <TextField select>
 *   <TextField select SelectProps={{ MenuProps: INLINE_SELECT_MENU_PROPS }} ... />
 */
export const INLINE_SELECT_MENU_PROPS = {
  anchorOrigin: { vertical: 'bottom', horizontal: 'left' },
  transformOrigin: { vertical: 'top', horizontal: 'left' },
  PaperProps: {
    elevation: 0,
    sx: {
      mt: 0.5,
      minWidth: 200,
      maxHeight: 320,
      borderRadius: 2,
      border: '1px solid',
      borderColor: 'divider',
      boxShadow: '0 8px 24px rgba(15,23,42,0.12)',
      overflowY: 'auto',
      p: 0.5,
      '& .MuiList-root': { p: 0 },
    },
  },
};

/**
 * Shared sx for the collapsed trigger of an inline `<Select>` that renders a
 * badge/chip as its value (removes default padding, reserves room for the
 * dropdown icon, and colours the caret on hover).
 */
export const INLINE_SELECT_TRIGGER_SX = {
  '& .MuiSelect-select': {
    display: 'flex',
    alignItems: 'center',
    p: 0,
    pr: '20px !important',
    minHeight: 'unset',
  },
  '& .MuiSelect-icon': { color: 'text.secondary', right: 0 },
  '&:hover .MuiSelect-icon': { color: 'text.primary' },
};

/**
 * Shared sx for a menu item inside an inline dropdown (rounded hover/selected
 * states, comfortable spacing). Pass `selected` to bump the font weight.
 */
export const inlineMenuItemSx = (selected = false) => ({
  gap: 1,
  borderRadius: 1.5,
  py: 0.75,
  px: 1,
  fontSize: '0.8rem',
  fontWeight: selected ? 700 : 500,
  '&.Mui-selected': { bgcolor: 'action.selected' },
  '&.Mui-selected:hover': { bgcolor: 'action.selected' },
});

export default INLINE_SELECT_MENU_PROPS;
